import React, {
	useCallback,
	useEffect,
	useRef,
	useState,
	useMemo,
} from "react";
import useFetch from "use-http";
import { useLocation } from "react-router-dom";
// Lazy-load pixelstreaming SDK to reduce main bundle size
const loadPixelStreamingSDK = async () => {
	const {
		ArcwareInit,
		ArcwarePixelStreaming,
		ArcwareApplication,
	} = await import("@arcware-cloud/pixelstreaming-websdk");
	return { ArcwareInit, ArcwarePixelStreaming, ArcwareApplication };
};
import { ACCESS_TOKEN_COOKIE } from "common/constants/cookies";
import { useCookies } from "react-cookie";
import PageQuery, { getQueryStringFromPageQuery } from "common/types/PageQuery";
import useProjects from "contexts/Projects/useProjects";
import { useOrganizations } from "contexts/Organizations";
import { SCENES } from "common/routes.constants";
import { PagedResponse } from "common/types/PagedResponse";
import isSceneProcessing from "utils/isSceneProcessing";
import {
	WEBSOCKET_EVENT_CODE_NO_STATUS,
	WEBSOCKET_EVENT_CODE_NORMAL,
} from "views/SharedScene/StreamingStatesConstants";
import { deepMerge } from "utils/mergeObjects";
import {
	captureViewpoint,
	goToCameraState,
	SCENE_THUMBNAIL_SNAPSHOT_SIZE,
} from "common/viewpointBridge";
import { cameraStateFromLegacyFields } from "views/Projects/views/Scene/tabs/Scene/Editor/r3f/utils/viewpointCamera";
import { getScenePositionOffset } from "common/sceneOffset";
import { useCameraStore } from "views/Projects/views/Scene/tabs/Scene/Editor/r3f/stores/useCameraStore";
import {
	attachStreamingDiagnostics,
	buildArcwareConfig,
	describeStreamingSession,
	getArcwareShareId,
	isFreshlyConnecting,
	isSceneLoadedResponse,
	LIVE_STREAMING_STATES,
	loveLetterToStatus,
	queueToStatus,
	startStreamingWatchdog,
	streamingError,
	streamingLog,
	streamingWarn,
	StreamingStatus,
	whenStreamerReady,
} from "components/PStreaming/streamingSession";
import {
	Scene,
	SceneExtended,
	BimcollabTokenData,
	LodType,
	Cesium,
	sceneDetailProps,
	SceneSaveResult,
} from "./types";
import Context from "./context";
import { hasOwnThumbnail, isPlanModeAvailable } from "./sceneCapabilities";
import { consumeSceneCreatedInFloorplanner } from "./newFloorplanSceneSignal";
import { useSceneDocStore, ScenePatch } from "./useSceneDocStore";
import useSceneLimitToast from "hooks/useSceneLimitToast";

const SCENE_POOLING_INTERVAL =
	Number(process.env.REACT_APP_SCENE_POOLING_INTERVAL) || 20000;

const isPlainObject = (value: unknown): boolean =>
	value !== null && typeof value === "object" && !Array.isArray(value);

/**
 * Builds the PUT body for a scene patch — the payload carries what the caller
 * changed and nothing else.
 *
 * The backend's scene `update` controller is already field-level for `meta`: it
 * copies over only the keys present in `req.body.meta`. So `meta` is sent as the
 * bare patch, which is what keeps a save that has nothing to do with the name
 * from carrying a name at all — previously every partial save round-tripped a
 * whole client-side copy of the document, so any stale field in that copy was
 * written back over the database.
 *
 * `scene` and `settings` are different: the controller assigns those wholesale,
 * so a partial there would drop everything omitted. They get merged onto the
 * live document first. Every other field (tokens, BIMprojectId, ...) is a flat
 * assignment on both sides and passes through untouched.
 */
const buildScenePatchPayload = (
	doc: SceneExtended | undefined,
	patch: ScenePatch,
): Record<string, unknown> => {
	const payload: Record<string, unknown> = {};
	const docFields = (doc as unknown) as Record<string, unknown> | undefined;
	Object.entries((patch as unknown) as Record<string, unknown>).forEach(([key, value]) => {
		const base = docFields?.[key];
		if (
			(key === "scene" || key === "settings") &&
			isPlainObject(base) &&
			isPlainObject(value)
		) {
			payload[key] = deepMerge(
				base as Record<string, unknown>,
				value as Record<string, unknown>,
			);
			return;
		}
		payload[key] = value;
	});
	return payload;
};

interface ScenesProviderProps {
	children?: React.ReactNode;
}

const ScenesProvider: React.FC<ScenesProviderProps> = ({ children }) => {
	const showSceneLimitToast = useSceneLimitToast();
	const { pathname } = useLocation();
	const { current: currentProject } = useProjects();
	const { current: currentOrganization, user } = useOrganizations();
	// `current` is held in useSceneDocStore rather than local state so that the
	// save callbacks below can read the live document instead of a render-time
	// closure. The context API is unchanged — consumers still get `current` and
	// `setCurrent` — plus `patchCurrent` for field-level updates.
	const current = useSceneDocStore((s) => s.doc);
	const setCurrent = useSceneDocStore((s) => s.setDoc);
	const patchCurrent = useSceneDocStore((s) => s.patchDoc);

	const [lodValue, setLodValue] = useState<LodType>(0);
	const [editorState, setEditorState] = useState<string | null>(null);
	const [editorMode, setEditorMode] = useState<'edit' | 'plan'>('edit');
	const [linkedFloorplan, setLinkedFloorplan] = useState<{ floors: Array<{ name: string; data: string; rooms?: any[] }> } | null>(null);

	// Scenes without a linked floorplan (model-import scenes) have no
	// floorplan-authored architecture for plan mode to operate on.
	const planModeAvailable = useMemo(() => isPlanModeAvailable(current), [current]);

	// Public setter: entering plan mode is a no-op when the current scene has
	// no linked floorplan, so callers (e.g. the "Modify Rooms" toggle) can't
	// switch into plan mode for a scene it doesn't apply to.
	const setEditorModeGuarded = useCallback((mode: 'edit' | 'plan') => {
		if (mode === 'plan' && !planModeAvailable) return;
		setEditorMode(mode);
	}, [planModeAvailable]);

	const [cookies] = useCookies([ACCESS_TOKEN_COOKIE]);

	const [scenes, setScenes] = useState<Scene[]>([]);
	const [BIMIssues, setBIMIssues] = useState<any>([]);
	const [viewpoints, setViewpoints] = useState<any | null>(null);
	const [viewpointBeingAdded, setViewpointBeingAdded] = useState<any | null>(
		null,
	);
	const [refreshTopics, setRefreshTopics] = useState<boolean>(false);
	const [scenesInProcess, setScenesInProcess] = useState<Scene[]>([]);
	const [
		currentBimcollab,
		setCurrentBimcollab,
	] = useState<BimcollabTokenData>();

	const [query, setQuery] = useState<PageQuery>({
		limit: 48,
		page: 1,
		search: {
			text: "",
			fields: [],
		},
		sort: "-meta.tsModified",
	});
	const [pagedResponse, setPagedResponse] = useState<PagedResponse<Scene>>();
	const [objectIdsInWishlists, setObjectIdsInWishlists] = useState<string[]>([]);
	const append = useRef<boolean>(false);

	// PIXEL STREAMING STATES
	const [state, setState] = useState<string | null>("welcome");
	const [orgProjScene, setOrgProjScene] = useState<string | null>(null);
	const [sceneLoaded, setSceneLoaded] = useState<boolean>(false);
	const [streamingResponse, setStreamingResponse] = useState<any | null>(
		null,
	);

	const [
		arcwareApplication,
		setArcwareApplication,
	] = useState<any | null>(null);
	const [
		pixelStreaming,
		setPixelStreaming,
	] = useState<any | null>(null);
	const [applicationResponse, setApplicationResponse] = useState("");
	const [
		streamingCloseEvent,
		setStreamingCloseEvent,
	] = useState<CloseEvent | null>(null);

	const isStage =
		!(process.env.NODE_ENV === "production") ||
		window?.location?.hostname?.includes("hegias-stage.com");

	// Flag to prevent setting streamingCloseEvent during intentional disconnect
	const isIntentionalDisconnect = useRef(false);

	// Flag to indicate streaming is being cleaned up (prevents starting new session too early)
	const [isStreamingCleaningUp, setIsStreamingCleaningUp] = useState(false);

	// Flag to prevent loadMore during project change
	const isProjectChanging = useRef(false);

	// Store handler references for proper cleanup
	const videoInitializedHandlerRef = useRef<any>(null);
	const websocketOnCloseHandlerRef = useRef<any>(null);
	const loveLetterHandlerRef = useRef<any>(null);
	const queueHandlerRef = useRef<any>(null);
	const webRtcDisconnectedHandlerRef = useRef<any>(null);

	// Latest SDK instances, for callbacks and timers that outlive a render
	const pixelStreamingRef = useRef<any>(null);
	const arcwareApplicationRef = useRef<any>(null);
	pixelStreamingRef.current = pixelStreaming;
	arcwareApplicationRef.current = arcwareApplication;

	// Cancels a scene-load command still waiting for the data channel
	const pendingSceneLoadRef = useRef<(() => void) | null>(null);

	// Next ArcwareInit must build new SDK instances instead of returning the singleton
	const forceFreshInitRef = useRef(false);

	// Guards the async SDK initialisation against the effect running twice
	// while the SDK chunk is still loading
	const initInFlightRef = useRef(false);

	// Removes the console diagnostics hooked into the SDK instance
	const diagnosticsDetachRef = useRef<(() => void) | null>(null);

	// What the cloud reports while the user waits: instance booting, queue
	const [streamingStatus, setStreamingStatus] = useState<StreamingStatus>(null);

	// PIXEL STREAMING FUNCTIONS AND HANDLERS

	const cancelPendingSceneLoad = () => {
		pendingSceneLoadRef.current?.();
		pendingSceneLoadRef.current = null;
	};

	const sendMessage = (message: any) => {
		(arcwareApplicationRef.current as any)?.emitUIInteraction(message);
	};

	/**
	 * Key of the scene a running session shows, or has been asked to show.
	 * Null while organization, project or scene are not all resolved, e.g.
	 * halfway through a project switch.
	 */
	const getCurrentSceneKey = (): string | null =>
		currentOrganization?._id && currentProject?._id && current?._id
			? `${currentOrganization._id}-${currentProject._id}-${current._id}`
			: null;

	/**
	 * Asks the connected Unreal instance to load the current scene.
	 *
	 * `orgProjScene` is set here, at send time, and means "the scene this
	 * session has been asked for": a scene switch that lands while the previous
	 * load is still running goes out too, and nothing is sent twice. The
	 * message itself leaves only once the data channel is open — see
	 * whenStreamerReady. Returns false when the scene data is not there yet;
	 * the calling effect runs again once it is.
	 */
	const sendSceneLoad = ({ forceupdate }: { forceupdate: boolean }): boolean => {
		const key = getCurrentSceneKey();
		const application = arcwareApplicationRef.current;
		if (!application || !key || !cookies[ACCESS_TOKEN_COOKIE]) {
			return false;
		}
		const message = {
			app: {
				shareid: "",
				sceneid: current._id,
				projectid: currentProject._id,
				organizationid: currentOrganization._id,
				isstage: isStage,
				forceupdate,
				bearer: cookies[ACCESS_TOKEN_COOKIE],
			},
		};
		cancelPendingSceneLoad();
		setOrgProjScene(key);
		setState("loadingScene");
		setSceneLoaded(false);
		streamingLog("scene load requested", {
			sceneid: current._id,
			projectid: currentProject._id,
			forceupdate,
		});
		pendingSceneLoadRef.current = whenStreamerReady(
			pixelStreamingRef.current,
			() => {
				pendingSceneLoadRef.current = null;
				streamingLog(
					"scene load command sent to the Unreal app",
					describeStreamingSession(pixelStreamingRef.current),
				);
				application.emitUIInteraction(message);
			},
		);
		return true;
	};

	/**
	 * Tell a running streaming session to load the current scene again.
	 *
	 * A plain scene switch sends the scene with `forceupdate: false`, and the
	 * app keeps whatever it read the first time: save a material, look at the
	 * Navigator, and it shows the scene as it was when the session started —
	 * the materials are in the record, just not in the session. `forceupdate`
	 * is what asks it to re-read rather than reuse.
	 */
	const reloadStreamingScene = () => {
		if (!LIVE_STREAMING_STATES.includes(state)) {
			// Nothing is listening: after an AFK disconnect the SDK's own
			// overlay is the way back in, and a command queued now would only
			// put a spinner over it.
			streamingWarn(`reload ignored, session is not live (state=${state})`);
			return;
		}
		sendSceneLoad({ forceupdate: true });
	};

	/**
	 * Every response from the Unreal app lands here, straight from the SDK
	 * callback. Scene-loaded detection happens in the callback rather than in
	 * an effect on `applicationResponse`: the app answers a reload, or a second
	 * session on the same scene, with exactly the string it sent before, and a
	 * state set to an identical string does not re-render — the spinner would
	 * never go away.
	 */
	const handleApplicationResponse = (response: string) => {
		streamingLog("Unreal app response", String(response ?? "").slice(0, 400));
		setApplicationResponse(response);
		if (isSceneLoadedResponse(response)) {
			setSceneLoaded(true);
			setState("sceneLoaded");
			setStreamingStatus(null);
		}
	};

	const detachStreamingHandlers = (ps: any) => {
		if (!ps) return;
		diagnosticsDetachRef.current?.();
		diagnosticsDetachRef.current = null;
		try {
			if (videoInitializedHandlerRef.current) {
				ps.videoInitializedHandler?.remove?.(videoInitializedHandlerRef.current);
				videoInitializedHandlerRef.current = null;
			}
			if (websocketOnCloseHandlerRef.current) {
				ps.websocketOnCloseHandler?.remove?.(websocketOnCloseHandlerRef.current);
				websocketOnCloseHandlerRef.current = null;
			}
			if (loveLetterHandlerRef.current) {
				ps.loveLetterHandler?.remove?.(loveLetterHandlerRef.current);
				loveLetterHandlerRef.current = null;
			}
			if (queueHandlerRef.current) {
				ps.queueHandler?.remove?.(queueHandlerRef.current);
				queueHandlerRef.current = null;
			}
			if (webRtcDisconnectedHandlerRef.current) {
				ps.removeEventListener?.("webRtcDisconnected", webRtcDisconnectedHandlerRef.current);
				webRtcDisconnectedHandlerRef.current = null;
			}
		} catch (e) {
			console.warn("[streaming] Error removing handlers:", e);
		}
	};

	/**
	 * Registers our handlers on a PixelStreaming/Application pair. Safe to call
	 * on every (re)start: previous handlers are removed first, and the SDK keys
	 * the response listener by name, so registering again replaces it instead
	 * of stacking.
	 */
	const attachStreamingHandlers = (ps: any, application: any) => {
		detachStreamingHandlers(ps);
		diagnosticsDetachRef.current = attachStreamingDiagnostics(ps);

		const videoInitializedHandler = () => {
			// The connection is up: from here on a close is a real close
			isIntentionalDisconnect.current = false;
			setStreamingStatus(null);
			setState("videoInitialized");
		};
		videoInitializedHandlerRef.current = videoInitializedHandler;
		ps.videoInitializedHandler.add(videoInitializedHandler);

		const websocketOnCloseHandler = (event: CloseEvent) => {
			// A close we asked for (disconnect, forced reconnect) uses code 1000
			if (isIntentionalDisconnect.current && event.code === WEBSOCKET_EVENT_CODE_NORMAL) {
				return;
			}
			// undefined: spurious SDK events during reconnection; 1005: closed without a status
			if (event.code === undefined || event.code === WEBSOCKET_EVENT_CODE_NO_STATUS) {
				return;
			}
			streamingError(
				`connection closed by the streaming side: code=${event.code} reason="${event.reason}"`,
				describeStreamingSession(ps),
			);
			cancelPendingSceneLoad();
			setStreamingCloseEvent(event);
		};
		websocketOnCloseHandlerRef.current = websocketOnCloseHandler;
		ps.websocketOnCloseHandler.add(websocketOnCloseHandler);

		const loveLetterHandler = (letter: any) => {
			const status = loveLetterToStatus(letter);
			if (status) setStreamingStatus(status);
		};
		loveLetterHandlerRef.current = loveLetterHandler;
		ps.loveLetterHandler?.add?.(loveLetterHandler);

		const queueHandler = (queueMessage: any) => {
			const status = queueToStatus(queueMessage);
			if (status) setStreamingStatus(status);
		};
		queueHandlerRef.current = queueHandler;
		ps.queueHandler?.add?.(queueHandler);

		// The SDK ends a session it considers over (AFK timeout, server-side
		// "stream disconnected") by closing only the signalling socket, with a
		// close event that carries no code — which the handler above ignores
		// by design. It then shows its own "click to restart" overlay (our
		// AFKOverlayMessage lives in it) and leaves the peer connection to
		// time out on its own, which is the "ICE failed" the browser logs
		// twenty seconds later. Mark the session as gone so a scene switch or
		// reload cannot queue a command nobody will read, and close the peer
		// connection the way the SDK's own disconnect() does.
		const webRtcDisconnectedHandler = (event: any) => {
			if (isIntentionalDisconnect.current) {
				return;
			}
			if (!event?.data?.allowClickToReconnect) {
				// The SDK is about to reconnect by itself; leave it to it
				return;
			}
			streamingWarn(
				`session ended by the streaming side: "${event.data.eventString}" — waiting for the user to restart`,
			);
			cancelPendingSceneLoad();
			setOrgProjScene(null);
			setStreamingStatus(null);
			setState("closedOnAfkError");
			try {
				ps.webRtcController?.closePeerConnection?.();
			} catch (e) {
				streamingWarn("closing the peer connection after the disconnect threw:", e);
			}
		};
		webRtcDisconnectedHandlerRef.current = webRtcDisconnectedHandler;
		ps.addEventListener?.("webRtcDisconnected", webRtcDisconnectedHandler);

		application.getApplicationResponse(handleApplicationResponse);
	};

	/**
	 * Closes the cloud session but keeps the SDK instances: the next start
	 * reconnects them, which is much quicker than building new ones.
	 */
	const closeWebRTCClient = () => {
		cancelPendingSceneLoad();
		setStreamingStatus(null);
		setOrgProjScene(null);
		setSceneLoaded(false);

		if (arcwareApplication || pixelStreaming) {
			// Keep the start button off until the SDK has torn the socket down
			setIsStreamingCleaningUp(true);

			// Our own close must not show up as an error
			isIntentionalDisconnect.current = true;

			// Remove our handlers BEFORE disconnect so they do not fire during cleanup
			detachStreamingHandlers(pixelStreaming);

			// Clear video element state before disconnect to help cleanup
			try {
				const videoElement = arcwareApplication?.rootElement?.querySelector('video');
				if (videoElement) {
					videoElement.pause();
					videoElement.srcObject = null;
				}
			} catch (e) {
				console.warn("[streaming] Error clearing video element:", e);
			}

			try {
				pixelStreaming?.disconnect();
			} catch (e) {
				console.warn("[streaming] Error during pixelStreaming disconnect:", e);
			}

			setStreamingCloseEvent(null);

			// The socket closes asynchronously; give the SDK a moment to settle
			// before a new session can be started. reconnect() copes with a
			// socket that is still CLOSING, so this is a courtesy, not a guard.
			setTimeout(() => {
				isIntentionalDisconnect.current = false;
				setIsStreamingCleaningUp(false);
			}, 1000);
		} else {
			console.log("[streaming] closeWebRTCClient: No instances to close");
		}
	};

	const setFlag = (flag: string, value: boolean) => {
		if (pixelStreaming) {
			(pixelStreaming.config as any)?.flags?.set(flag, value);
		}
	};

	/**
	 * Sends a patch payload for the open scene.
	 *
	 * The scene id is read from the store rather than from `current`, so a save
	 * fired from a callback that rendered a moment ago still targets the scene
	 * that is actually open.
	 */
	const saveScene = useCallback(
		async (payload: Record<string, unknown>): Promise<SceneSaveResult> => {
			const doc = useSceneDocStore.getState().doc;
			if (!doc || !currentProject) return { ok: false };
			putSceneErrorRef.current = null;
			const result = await putScene(
				`/projects/${currentProject._id}/scenes/${doc._id}`,
				payload,
			);
			// use-http does not throw on a 4xx/5xx (and the configured provider
			// adds no interceptor that would), so the outcome has to be read here.
			// This used to return true unconditionally: a rejected save showed
			// "saved", was folded into the store and marked the editor clean.
			if (putSceneErrorRef.current || !putSceneResponse.ok) {
				return {
					ok: false,
					message: result?.translatedMessage
						|| (typeof result?.message === "string" ? result.message : undefined),
				};
			}
			return { ok: true };
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentProject],
	);

	/**
	 * The one way to persist part of the open scene: send only `patch`, and fold
	 * the same patch into the store so client and database stay in step. There is
	 * deliberately no "save the whole document" path — that is what allowed a
	 * stale copy to revert fields nobody had touched.
	 */
	const saveScenePatch = useCallback(
		async (patch: ScenePatch): Promise<SceneSaveResult> => {
			const doc = useSceneDocStore.getState().doc;
			if (!doc || !patch) return { ok: false };
			const saved = await saveScene(buildScenePatchPayload(doc, patch));
			if (saved.ok) useSceneDocStore.getState().patchDoc(patch);
			return saved;
		},
		[saveScene],
	);

	/**
	 * "Save as": server-side this copies the open scene's document and its S3
	 * assets into a brand new scene, names the copy, and replaces the copy's
	 * content with `sceneContent` when given.
	 *
	 * Deliberately not routed through saveScenePatch. Everything else here edits
	 * the open scene; this one writes to a scene that doesn't exist yet, so it
	 * needs its own verb (the endpoint is a POST), its own body shape (a flat
	 * `name`, not `meta.name`), and — most importantly — must leave the scene
	 * store alone, since the open scene is not what changed.
	 *
	 * The endpoint returns only a status message, not the new scene, so callers
	 * that want to show or open the copy have to refresh the scene list.
	 */
	const saveSceneAs = useCallback(
		async (name: string, sceneContent?: unknown): Promise<boolean> => {
			const doc = useSceneDocStore.getState().doc;
			if (!doc || !currentProject || !name?.trim()) return false;
			const result = await postScene(
				`/projects/${currentProject._id}/scenes/${doc._id}/save-as`,
				{
					name: name.trim(),
					...(sceneContent ? { scene: sceneContent } : {}),
				},
			);
			// A b2c account at its three-scene cap gets a 403 here. Report the
			// failure rather than the previous unconditional true, so the caller
			// does not go on to refresh a list that gained nothing.
			if (showSceneLimitToast(saveAsResponse, result)) return false;
			return true;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentProject, showSceneLimitToast],
	);

	const saveEditorScene = useCallback(
		async (sceneEditor: Partial<SceneExtended>): Promise<SceneSaveResult> => {
			if (!sceneEditor) return { ok: false };

			const sceneEditorCopy = { ...sceneEditor };
			if (sceneEditorCopy.scene?.params?.cesium) {
				delete sceneEditorCopy.scene?.params?.cesium;
			}
			delete sceneEditorCopy.scene?.params?.isnavwhitemodel;
			// sceneEditor.meta (e.g. a first-save rename) is intentionally kept: it
			// travels as the bare patch the backend merges field-by-field, so passing
			// { name } here changes only the name. An editor save that carries no
			// meta at all sends none — the backend stamps meta.tsModified itself.

			// Returned, not just awaited: the editor's save has to know whether the
			// backend accepted it before it marks anything clean (and Live View
			// before it opens the Navigator on the saved state).
			return saveScenePatch(sceneEditorCopy as ScenePatch);
		},
		[saveScenePatch],
	);

	const saveLocationScene = useCallback(
		async (sceneLocation: Cesium) => {
			if (!sceneLocation) return;
			await saveScenePatch({ scene: { params: { cesium: sceneLocation } } });
		},
		[saveScenePatch],
	);

	const saveDetailScene = useCallback(
		async (sceneDetail: sceneDetailProps) => {
			if (!sceneDetail) return;
			await saveScenePatch(sceneDetail as ScenePatch);
		},
		[saveScenePatch],
	);

	const savePartialSceneInformation = useCallback(
		async (scenePartialInformation: Partial<SceneExtended>) => {
			if (!scenePartialInformation) return;
			await saveScenePatch(scenePartialInformation as ScenePatch);
		},
		[saveScenePatch],
	);

	// Track organization changes (full reset needed)
	const orgKey = currentOrganization?._id;
	const prevOrgKeyRef = useRef<string | null>(null);

	useEffect(() => {
		// Check if organization changed (not project or scene)
		if (prevOrgKeyRef.current && prevOrgKeyRef.current !== orgKey) {
			closeWebRTCClient();
			setState("welcome");
			setStreamingCloseEvent(null);
			setApplicationResponse("");
		}
		prevOrgKeyRef.current = orgKey;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orgKey]);

	// The provider unmounts when the user leaves the projects area. Without
	// this the cloud session keeps rendering (and billing) into a detached
	// video element, and the SDK singleton comes back on the next start with
	// an open socket that never fires videoInitialized again.
	useEffect(
		() => () => {
			cancelPendingSceneLoad();
			detachStreamingHandlers(pixelStreamingRef.current);
			try {
				pixelStreamingRef.current?.disconnect?.();
			} catch (e) {
				console.warn("[streaming] Error disconnecting on unmount:", e);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	// Every state transition, with where the SDK stands at that moment
	useEffect(() => {
		streamingLog(`state -> ${state}`, describeStreamingSession(pixelStreamingRef.current));
	}, [state]);

	// While the spinner is up, say every ten seconds what we are waiting for
	useEffect(() => {
		const waiting =
			["initializingStreaming", "loading", "videoInitialized", "loadingScene"].includes(state) &&
			!sceneLoaded &&
			!streamingCloseEvent;
		if (!waiting) {
			return undefined;
		}
		return startStreamingWatchdog(() => ({
			state,
			requestedScene: orgProjScene,
			sceneLoadPending: !!pendingSceneLoadRef.current,
			...describeStreamingSession(pixelStreamingRef.current),
		}));
	}, [state, sceneLoaded, streamingCloseEvent, orgProjScene]);

	// Scene switch (scene switcher, browser back/forward) while a session is
	// live: keep the connection and only send a load command. Nothing happens
	// while the session is not live — a closed session used to be sent the
	// command anyway, and jumped from the welcome screen into a spinner with
	// no stream behind it, which only a close-and-restart got out of.
	useEffect(() => {
		if (!LIVE_STREAMING_STATES.includes(state) || streamingCloseEvent) {
			return;
		}
		const key = getCurrentSceneKey();
		if (!orgProjScene || !key || orgProjScene === key) {
			return;
		}
		sendSceneLoad({ forceupdate: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		state,
		orgProjScene,
		streamingCloseEvent,
		current?._id,
		currentProject?._id,
		currentOrganization?._id,
	]);

	useEffect(() => {
		if (state === "readyToInit") {
			if (initInFlightRef.current) {
				return;
			}
			setStreamingCloseEvent(null);
			setStreamingStatus(null);
			setSceneLoaded(false);
			setOrgProjScene(null);

			// SDK instances from a previous session: reconnect them
			if (pixelStreaming && arcwareApplication) {
				streamingLog("start: reconnecting existing SDK instances");
				attachStreamingHandlers(pixelStreaming, arcwareApplication);

				// Clear video element state before reconnect to prevent fetch abort issues
				try {
					const videoElement = arcwareApplication.rootElement?.querySelector('video');
					if (videoElement) {
						videoElement.pause();
						videoElement.srcObject = null;
						videoElement.load();
					}
				} catch (e) {
					console.warn("[streaming] Error clearing video element:", e);
				}

				// reconnect() copes with every socket state: CLOSED connects
				// straight away, OPEN or CLOSING closes first and connects two
				// seconds later. The close it triggers is ours, so mute it.
				isIntentionalDisconnect.current = true;
				setTimeout(() => {
					try {
						pixelStreaming.reconnect();
					} catch (e) {
						console.warn("[streaming] reconnect() threw, rebuilding SDK instances:", e);
						isIntentionalDisconnect.current = false;
						detachStreamingHandlers(pixelStreaming);
						forceFreshInitRef.current = true;
						setArcwareApplication(null);
						setPixelStreaming(null);
						setTimeout(() => setState("readyToInit"), 500);
						return;
					}
					setTimeout(() => {
						isIntentionalDisconnect.current = false;
					}, 2000);
				}, 200);

				setState("initializingStreaming");
				return;
			}

			// No instances in React state: ask the SDK for some
			initInFlightRef.current = true;
			(async () => {
				try {
					const { ArcwareInit } = await loadPixelStreamingSDK();
					const forceFresh = forceFreshInitRef.current;
					forceFreshInitRef.current = false;
					streamingLog("start: ArcwareInit", {
						shareId: getArcwareShareId(isStage),
						isStage,
						forceFresh,
					});
					const { PixelStreaming, Application } = ArcwareInit(
						{ shareId: getArcwareShareId(isStage) },
						buildArcwareConfig() as any,
						forceFresh,
					);
					streamingLog(
						`ArcwareInit returned (freshly connecting: ${isFreshlyConnecting(PixelStreaming)})`,
						describeStreamingSession(PixelStreaming),
					);

					attachStreamingHandlers(PixelStreaming, Application);
					setArcwareApplication(Application);
					setPixelStreaming(PixelStreaming);

					// ArcwareInit constructs (and AutoConnects) once per share
					// id; afterwards it hands back the same singleton. That is
					// what we get whenever this provider was unmounted in
					// between: its socket may be open, closed or half-way, and
					// videoInitialized has already been sent once, so nothing
					// would ever move us past "initializing". A reconnect
					// resets those guards and connects.
					if (!isFreshlyConnecting(PixelStreaming)) {
						streamingWarn("SDK singleton reused, forcing reconnect()");
						isIntentionalDisconnect.current = true;
						try {
							PixelStreaming.reconnect();
						} catch (e) {
							streamingError("reconnect() on reused SDK singleton threw:", e);
						}
						setTimeout(() => {
							isIntentionalDisconnect.current = false;
						}, 2000);
					}

					setState("initializingStreaming");
				} catch (e) {
					streamingError("Failed to initialise the streaming SDK:", e);
					setState("welcome");
				} finally {
					initInFlightRef.current = false;
				}
			})();
		} else if (state === "videoInitialized") {
			// Returns false while organization, project or scene are still
			// loading; this effect runs again as soon as they are there.
			sendSceneLoad({ forceupdate: false });
		}
		// Keyed on the scene id, not the whole document: this state machine only
		// ever reads current?._id, and the document's identity now changes on
		// every save (the store is patched with what was persisted), which must
		// not re-drive streaming state.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state, currentOrganization, currentProject, current?._id, isStage, cookies]);

	const { get: getScenes, loading, error } = useFetch();

	const {
		get: getScene,
		loading: currentLoading,
		error: currentSceneError,
	} = useFetch(`/projects/${currentProject?._id}/scenes`);

	const { get: getFloorplan } = useFetch(`/projects/${currentProject?._id}/floorplans`);

	const { post: createShareToken } = useFetch(
		`/projects/${currentProject?._id}/scenes/${current?._id}/token`,
	);

	const { get: getSceneStatus } = useFetch(
		`/projects/${currentProject?._id}/scenes`,
	);

	const { put, post: postScene, response: saveAsResponse } = useFetch();

	// The scene save's own instance, so the response read after it (see
	// saveScene) is that request's and not a save-as or updateScene that
	// happened to run on the shared one. onError is the only failure signal
	// that covers a network error too: use-http keeps the previous request's
	// response in that case, so `response.ok` alone could still read true.
	const putSceneErrorRef = useRef<Error | null>(null);
	const { put: putScene, response: putSceneResponse } = useFetch({
		onError: ({ error: putError }: { error: Error }) => {
			putSceneErrorRef.current = putError;
		},
	});

	// Scene thumbnail upload. Mirrors the Details tab's manual button
	// (Details.tsx): the globalOptions override drops Content-Type so the browser
	// can set the multipart boundary itself, while everything the FetchProvider
	// puts on a request is kept — including the `x-org` header, without which the
	// upload endpoint cannot resolve the caller's rights on the scene and answers
	// 404. A bare fetch() carrying only Authorization is NOT enough here, even
	// though it is for the neighbouring /poi calls.
	const { post: postThumbnail, response: thumbnailResponse } = useFetch(
		(globalOptions: any) => {
			const newGlobalOptions = { ...globalOptions };
			delete newGlobalOptions.headers["Content-Type"];
			return newGlobalOptions;
		},
	);

	const setCurrentScene = async (shortid?: string) => {
		const match = scenes?.find((s) => s.shortid === shortid);
		logger.debug("[ScenesProvider] setCurrentScene", { shortid, match });

		// can not fetch random scene ID, has to come from the current array of scenes
		if (match) {
			const scene = await getScene(`/${match._id as string}?includeFloorplan=true`);
			if (scene) {
				setLodValue(scene.settings?.lod ?? 0);
				setCorrectLodScene(scene, scene.settings?.lod);
			}
		} else {
			setCurrent(undefined);
			setViewpoints(null);
		}
	};

	// Fetch linked floorplan whenever the current scene changes
	useEffect(() => {
		setLinkedFloorplan(null);
		setEditorMode('edit');
		// plan.floorplanId is populated as { _id, shortid } — use _id since the controller uses findById
		const populated = current?.plan?.floorplanId;
		const floorplanId = populated && typeof populated === 'object' ? populated._id : undefined;
		if (!floorplanId || !currentProject?._id) return;
		getFloorplan(`/${floorplanId}`)
			.then((fp: any) => fp && setLinkedFloorplan(fp))
			.catch(() => setLinkedFloorplan(null));
	}, [current?.shortid]); // eslint-disable-line react-hooks/exhaustive-deps

	// Open in plan mode ONLY for a scene the user has just created via "Draw
	// floorplan myself" — it holds nothing but a seeded template room and the
	// template picker is about to open over it.
	//
	// This used to trigger on any floorplan-backed scene with no 3D children,
	// which read "empty scene" as "brand new". It is not the same thing: an
	// existing floorplan the user has not yet furnished is empty too, and landing
	// in plan mode every time they opened it was wrong. The effect above has
	// already set 'edit' for this scene, so doing nothing here is the default.
	useEffect(() => {
		if (!linkedFloorplan) return;
		if (consumeSceneCreatedInFloorplanner(current?.shortid)) {
			setEditorMode('plan');
		}
	}, [linkedFloorplan]); // eslint-disable-line react-hooks/exhaustive-deps

	const getSceneShortid = (_pathname: string): string => {
		const chunks = _pathname.split("/");
		const lastTwo = chunks.slice(-2);

		try {
			const [entity, shortid] = lastTwo;

			if (entity === SCENES && !!shortid) {
				return shortid;
			}

			return "";
		} catch {
			return "";
		}
	};

	const memoizedGetSceneShortId = useCallback(
		(_pathname: any) => getSceneShortid(_pathname),
		[],
	);

	const fetchScenes = async () => {
		const queryString = getQueryStringFromPageQuery(query);
		const response = await getScenes(
			`/projects/${currentProject?._id}/scenes?${queryString}`,
		);

		const results = append.current
			? [...scenes, ...response.results]
			: response.results;

		setScenes(results);
		setPagedResponse(response);
		append.current = false;
	};

	const loadMore = () => {
		// Block loadMore during project change transition
		if (isProjectChanging.current) {
			// console.log("[scenes] loadMore: Blocked during project change");
			return;
		}

		if (!pagedResponse?.hasNextPage) {
			// console.log("[scenes] loadMore: No next page available");
			return;
		}

		// Don't load more if we don't have any scenes yet (might be during project switch)
		if (scenes.length === 0) {
			// console.log("[scenes] loadMore: Ignoring, no scenes loaded yet");
			return;
		}

		// Don't load more if we're already on page 1 (indicates fresh fetch)
		if (query.page === 1 && !pagedResponse) {
			// console.log("[scenes] loadMore: Ignoring, still on initial page");
			return;
		}

		// console.log("[scenes] loadMore: Loading page", query.page + 1);
		append.current = true;
		setQuery((previousQuery) => ({
			...previousQuery,
			page: previousQuery.page + 1,
		}));
	};

	const fetchSceneStatus = async (id: string) => {
		const response = await getSceneStatus(`/${id}/status`);
		return response.status;
	};

	const encodeData = (data: string): string => {
		const encodedData = btoa(data);
		return encodedData;
	};

	const sendTrackingMessage = async (eventType: string, message: any = {}) => {
		if (!eventType) {
			return;
		}
		const ts = Math.floor(new Date().getTime() / 1000);
		const trackingMessage = {
			app_id: "HEGIAS.creator",
			send_ts: ts,
			events: [
				{
					user_id: user?._id,
					scene_id: current?._id,
					org_id: currentOrganization?._id,
					comment: (message && message.comment) || "",
					event_message: JSON.stringify(message || {}),
					event_start_ts: ts,
					event_end_ts: ts,
					event_type: eventType,
				},
			],
			// app_version: "",
			// organizationID: currentOrganization?._id,
			// projectID: currentProject?._id,
			// sceneID: current?._id,
			// isStage,
		};

		const response = await fetch(
			isStage
				? `https://ihjyd7jnv3ojmd4kbf2ebap2oa0nmeuk.lambda-url.eu-west-1.on.aws`
				: `https://hlbsq5djemymf5savmkx3vtjzu0biyqo.lambda-url.eu-west-1.on.aws/`,
			{
				method: "POST",
				mode: "no-cors",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${cookies[ACCESS_TOKEN_COOKIE]}`,
					"Access-Control-Allow-Origin": "*",
				},
				body: encodeData(JSON.stringify(trackingMessage)),
			},
		);

		// const data = await response.json();
		// return data;
	};

	const fetchViewpoints = async () => {
		try {
			// console.log("Fetching viewpoints");
			const response = await fetch(
				// '/projects/:id/scene/:sceneID/poi'
				`${process.env.REACT_APP_BASE_URL}/projects/${currentProject?._id}/scene/${current?._id}/poi`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${cookies[ACCESS_TOKEN_COOKIE]}`,
					},
				},
			);

			const data = response.ok ? await response.json() : null;
			// `viewpoints` stays null only while a fetch is in flight — the
			// Viewpoints panel spins on null and enables its "+" button on an
			// array. A failed request (the API 502s under load) or an error body
			// instead of a list must therefore still land on an array, or the
			// panel spins forever.
			if (!Array.isArray(data)) {
				console.error(
					"[ScenesProvider] fetchViewpoints got no list",
					response.status,
					data,
				);
				setViewpoints([]);
				return [];
			}
			setViewpoints(data);
			return data;
		} catch (err) {
			console.error("fetchViewpoints ERROR-->", err);
			setViewpoints([]);
			return [];
		} finally {
			// setLoadingViewpoints(false);
		}
	};

	const updateViewpoint = async (viewpoint: any) => {
		try {
			const response = await fetch(
				// '/projects/:id/scene/:sceneID/poi'
				`${process.env.REACT_APP_BASE_URL}/projects/${currentProject?._id}/poi/${viewpoint._id}`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${cookies[ACCESS_TOKEN_COOKIE]}`,
					},
					body: JSON.stringify(viewpoint),
				},
			);

			const data = await response.json();
			return data;
		} catch (err) {
			console.error("updateViewpoint ERROR-->", err);
		} finally {
			// nothing to do here
		}
	};

	const updateViewpointOrder = async (poiOrderPairs: any[]) => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/projects/${currentProject?._id}/poi`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${cookies[ACCESS_TOKEN_COOKIE]}`,
					},
					body: JSON.stringify({ pois: poiOrderPairs }),
				},
			);

			const data = await response.json();

			if (!response.ok) {
				console.error("[updateViewpointOrder] Error response:", response.status, data);
			}

			return data;
		} catch (err) {
			console.error("updateViewpointOrder ERROR-->", err);
		} finally {
			// nothing to do here
		}
	};

	const goToViewpoint = (object: any, animated = true) => {
		// Upload-based scenes were centered by the pipeline, but their viewpoints
		// are stored in the original, un-centered frame — without adding that
		// translation back the camera lands below and beside the room.
		const state = cameraStateFromLegacyFields(object, getScenePositionOffset(current));
		const leavingPlanMode = editorMode !== 'edit';

		const navigate = () => {
			// Leaving Plan Mode is itself a mode switch (an entirely different
			// view), even when the camera mode it restores happens to equal the
			// viewpoint's own cameraMode (e.g. both 'walk') — in that case
			// goToCameraState's own isModeSwitch check sees no mode change and
			// would animate a long tween from the plan-mode camera position
			// instead of snapping. Force a snap here instead.
			goToCameraState(state, leavingPlanMode ? false : animated).catch((err) => {
				console.error("goToViewpoint ERROR-->", err);
			});
		};

		// Viewpoints are an Edit Mode concept only — clicking one while in Plan
		// Mode must switch back to Edit Mode first so we don't mix the two.
		// VRContext's plan-mode-exit effect (which restores the camera mode that
		// was active before plan mode) runs in response to the setEditorMode()
		// call below — without the pendingArrival signal it would race our own
		// upcoming mode switch and clobber the viewpoint's target mode/position
		// right after we land on it. Pre-setting pendingArrival here, before
		// setEditorMode(), tells that effect to stand down (see VRContext.tsx);
		// useViewpointBridge re-sets it with the same data once it runs the
		// actual mode switch.
		if (leavingPlanMode) {
			useCameraStore.getState().setPendingArrival({
				position: state.position,
				quaternion: state.quaternion,
				target: state.target,
			});
			setEditorMode('edit');
			requestAnimationFrame(navigate);
		} else {
			navigate();
		}
	};

	useEffect(() => {
		if (editorState === "loadingScene" && !viewpoints) {
			fetchViewpoints();
		}
		if (editorState === "done" && viewpoints?.length > 0) {
			// goToViewpoint(viewpoints[0], false);				DO NOT go to first viewpoint in Editor automatically, change of thought
		}
	}, [editorState, viewpoints]);

	const updateScene = async () => {
		if (!current) {
			return;
		}

		let response;
		if (current && currentProject) {
			response = await put(
				`/projects/${currentProject._id}/scenes/${current._id}`,
				current,
			).then((data) => {

				// console.log("Scene updated", data);
				return data;
			});
		}
	};

	/**
	 * Adopt the frame a viewpoint was created from as the scene's thumbnail.
	 *
	 * Same endpoint and payload the Details tab's manual "update thumbnail"
	 * button uses (Details.tsx), fed from a snapshot captured at the moment the
	 * viewpoint was — so the thumbnail is exactly the view the user saved, and
	 * no second round-trip to the Canvas is needed here.
	 *
	 * The viewpoint's own snapshot is far too small to reuse here — it is capped
	 * at 300x200 because it rides along inside the scene document as base64 —
	 * so this takes its own 600x332 read of the canvas, which is exactly what
	 * the server stores. It is only a fallback source if that read fails.
	 *
	 * Deliberately NOT captured up in Viewpoints.tsx alongside the viewpoint's
	 * own snapshot: that put a second canvas read in front of every viewpoint
	 * creation, including the overwhelming majority that never set a thumbnail,
	 * and the new viewpoint's card could not appear until both had finished.
	 * Here it runs once, only for the first viewpoint of a scene without a
	 * thumbnail, and only after the card is already on screen. The camera cannot
	 * meaningfully drift in between — the user has just clicked a button.
	 *
	 * Best-effort on purpose: this is a convenience on top of creating a
	 * viewpoint, so a failure is logged and swallowed rather than surfaced. The
	 * viewpoint itself is already saved by the time this runs, and the user can
	 * still set the thumbnail by hand in the Details tab.
	 */
	const adoptViewpointSnapshotAsThumbnail = async (snapshotBase64?: string) => {
		if (!current?._id || !currentProject?._id) return;
		try {
			// meta.snapshot is the base64 payload with the data-URL prefix
			// stripped (see Viewpoints.tsx); captureViewpoint returns a full data
			// URL. Either way it is JPEG, and the server re-encodes to PNG.
			const source = await captureViewpoint(SCENE_THUMBNAIL_SNAPSHOT_SIZE)
				.then((r) => r.snapshot)
				.catch(() =>
					snapshotBase64
						? `data:image/jpeg;base64,${snapshotBase64}`
						: undefined,
				);
			if (!source) return;

			const blob = await (await fetch(source)).blob();
			const formData = new FormData();
			formData.append("thumbnail", blob, "thumbnail.jpg");

			const res = await postThumbnail(
				`/projects/${currentProject._id}/scenes/${current._id}/thumbnail`,
				formData,
			);
			if (!thumbnailResponse.ok) {
				throw new Error(
					`thumbnail upload returned ${thumbnailResponse.status}`,
				);
			}

			if (!res?.filename) return;

			// Patch just the filename, so nothing else in the scene document is
			// clobbered. Two separate stores hold a copy of it and both need it:
			// the scene doc behind the Details tab, and the `scenes` list the
			// project's scene gallery renders its cards from — which would
			// otherwise keep showing the placeholder until the next fetchScenes.
			patchCurrent({ meta: { thumbnail: res.filename } });
			setScenes((prev) =>
				prev.map((scene) =>
					scene._id === current._id
						? { ...scene, meta: { ...scene.meta, thumbnail: res.filename } }
						: scene,
				),
			);
		} catch (err) {
			console.error(
				"[ScenesProvider] couldn't adopt the first viewpoint as scene thumbnail",
				err,
			);
		}
	};

	const addViewpoint = async (viewpoint: any) => {
		if (!currentProject || !current) {
			console.error(
				"Current scene or project not set - can't add viewpoint.",
				viewpoint,
			);
			return;
		}
		try {
			const newViewpoint = {
				...viewpoint,
				SceneID: current._id,
				ProjectID: currentProject._id,
				isActive: true,
				Order: viewpoints?.length || 1,
			};
			// console.log("Adding new viewpoint", newViewpoint);

			// Save in backend
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/projects/${currentProject?._id}/poi`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${cookies[ACCESS_TOKEN_COOKIE]}`,
					},
					body: JSON.stringify(newViewpoint),
				},
			);

			const data = await response.json();
			// console.log("New viewpoint saved:", data);

			// Nothing below may run on a failed save: appending the error body to
			// the list would show a viewpoint that does not exist, and adopting a
			// thumbnail for it would be worse still. The caller has already put a
			// placeholder card up (setViewpointBeingAdded), so clear that too.
			if (!response.ok) {
				console.error(
					"[ScenesProvider] viewpoint POST failed",
					response.status,
					data,
				);
				setViewpointBeingAdded(null);
				return undefined;
			}

			// Convenience: the first viewpoint a user saves is almost always a
			// good cover image for the scene, so it becomes the thumbnail while
			// the scene still has only the schema's '../scene.jpg' placeholder
			// (hasOwnThumbnail — a plain truthiness check would never fire).
			// Never an overwrite: a thumbnail set by hand, or produced by the
			// import pipeline, stays put, and so does one whose first viewpoint
			// has since been deleted and re-created.
			// The user can still change it at will in the Details tab.
			//
			// `viewpoints` is still the pre-add list here (the state update below
			// is what appends to it), so an empty one means this is the first.
			// Array.isArray, not a falsy check: the list is null until
			// fetchViewpoints has run, and "not loaded yet" is not "none".
			const isFirstViewpoint =
				Array.isArray(viewpoints) && viewpoints.length === 0;
			if (isFirstViewpoint && !hasOwnThumbnail(current)) {
				void adoptViewpointSnapshotAsThumbnail(newViewpoint?.meta?.snapshot);
			}

			/* TODO: update viewpoint in DB with new name
			if (data.meta) {
				data.meta.name = `Viewpoint ${data.CreationNumber}`;
			}
			// todo: update viewpoint in DB with new name
			*/

			// add to list in CMS and allow adding another viewpoint only after half a second
			setTimeout(() => {
				// Functional update, and `?? []` for the case where the list is
				// still null: this runs inside a timeout, long after the closure
				// captured `viewpoints`, and spreading null threw a TypeError that
				// the try/catch around it cannot see — the viewpoint was saved but
				// never appeared in the list.
				setViewpoints((prev: any) => [...(prev ?? []), data]);
				setViewpointBeingAdded(null);
			}, 100);
			return data;
		} catch (err) {
			// A rejected fetch — the API 502ing, CORS, an offline client — lands
			// here rather than in the !response.ok branch above, so the caller's
			// placeholder card has to be cleared here too. Without it a viewpoint
			// that was never saved sits in the list until the scene is switched.
			console.error("addViewpoint ERROR-->", err);
			setViewpointBeingAdded(null);
			return undefined;
		}
	};

	const deleteViewpoint = async (viewpoint: any) => {
		if (!currentProject || !current) {
			console.error(
				"Current scene or project not set - can't delete viewpoint.",
				viewpoint,
			);
			return;
		}
		try {
			console.log("Deleting viewpoint", viewpoint);

			// Save in backend
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/projects/${currentProject?._id}/poi/${viewpoint._id}`,
				{
					method: "DELETE",
					headers: {
						Authorization: `Bearer ${cookies[ACCESS_TOKEN_COOKIE]}`,
					},
				},
			);

			const data = await response.json();
			// console.log("Viewpoint deleted:", data);

			// remove from list in CMS
			const newViewpoints = viewpoints.filter(
				(vp: any) => vp._id !== viewpoint._id,
			);
			setViewpoints(newViewpoints);
			return data;
		} catch (err) {
			console.error("deleteViewpoint ERROR-->", err);
		}
	};

	const createShareLink = async (tokenInfo: any) => {
		let result = [];

		result = await createShareToken({
			token: tokenInfo,
		});

		return result;
	};

	// Reset query to page 1 when project changes
	useEffect(() => {
		if (currentProject?._id) {
			isProjectChanging.current = true; // Block loadMore during transition
			append.current = false; // Ensure we replace rather than append
			setQuery((prev) => ({
				...prev,
				page: 1,
			}));

			// Clear the flag after scenes are loaded
			setTimeout(() => {
				isProjectChanging.current = false;
				// console.log("[scenes] Project change transition complete");
			}, 1000);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentProject?._id]);

	useEffect(() => {
		if (currentProject?._id) {
			// console.log("[scenes] Clearing scenes array");
			setScenes([]);
			fetchScenes();
		} else {
			// console.log("[scenes] No project, clearing scenes");
			setScenes([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentProject?._id, query]);

	useEffect(() => {
		if (!currentProject || !scenes?.length) {
			setCurrentScene("");
			return;
		}

		const sceneShortId = memoizedGetSceneShortId(pathname);
		if (current?.shortid !== sceneShortId) {
			setCurrent(undefined);
			setViewpoints(null);
		}
		logger.debug("[ScenesProvider] sceneShortId", {
			pathname,
			sceneShortId,
			currentProject,
			scenes,
		});
		setCurrentScene(sceneShortId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, currentProject, scenes]);

	// Extract & save scenes in process
	useEffect(() => {
		const processing = scenes?.filter(isSceneProcessing);
		if (processing) {
			setScenesInProcess(processing);
		}
	}, [scenes]);

	// Handle pooling for scenes in process
	useEffect(() => {
		const tick = async () => {
			const updatedScenesInProcess: Scene[] = await Promise.all(
				scenesInProcess.map(async (scene: Scene) => ({
					...scene,
					pipelineStatusSummary: await fetchSceneStatus(scene._id),
				})),
			);

			// Compare all objects in all processing scenes if they have changed the status
			const scenesMatch = updatedScenesInProcess.every((bScene) => {
				const aScene = scenes.find(
					(s) => s._id === bScene._id,
				) as Scene;
				const match =
					aScene.pipelineStatusSummary ===
					bScene.pipelineStatusSummary;

				return match;
			});

			logger.debug("[ScenesProvider] tick", {
				scenes,
				scenesInProcess,
				updatedScenesInProcess,
				scenesMatch,
			});

			// replace all scenes with the match from `scenesInProcess` because of status change
			if (!scenesMatch) {
				const updatedScenes = scenes.map((scene) => {
					const updateCandidate = updatedScenesInProcess.find(
						(updatedScene) => updatedScene._id === scene._id,
					);
					if (updateCandidate) {
						return {
							...scene,
							pipelineStatusSummary:
								updateCandidate.pipelineStatusSummary,
						};
					}

					return scene;
				});

				logger.info("[ScenesProvider] updating scenes list", {
					updatedScenes,
				});

				setScenes(updatedScenes);

				fetchScenes();
			}
		};

		const id = setInterval(tick, SCENE_POOLING_INTERVAL);

		return () => {
			clearInterval(id);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scenesInProcess]);

	/* LOD settings */

	/**
	 * Calculates the maximum number of Levels of Detail (LODs) available in the current scene.
	 *
	 * This function uses `useMemo` to memoize the result, ensuring it only recalculates when the `current` dependency changes.
	 *
	 * - Resets the streaming close event to `null` when the scene is loaded or switched.
	 * - Logs the updated available LODs for debugging purposes.
	 * - Iterates through the children of the current scene to determine the maximum number of LODs.
	 *
	 * @returns The maximum number of LODs available in the current scene. Returns `0` if the scene or its children are not available.
	 *
	 * @dependency `current` - The current scene object whose children are analyzed for LODs.
	 */
	const availableLods = useMemo(() => {
		// At scene load or switch: reset streaming close event, if any
		setStreamingCloseEvent(null);

		if (!current) {
			return 0;
		}
		if (!current.scene.children) {
			return 0;
		}
		let max = 0;

		current.scene.children.forEach((child: any) => {
			const numberOfLod = child.lod?.length ?? 0;
			if (numberOfLod > max) {
				max = numberOfLod;
			}
		});

		return max;
	}, [current]);

	const setCorrectLodScene = (scene: SceneExtended, lod?: number) => {
		const currentLod = lod ?? lodValue;

		if (!scene.scene.children) {
			setCurrent(scene);
			setViewpoints(null);
		}
		const newSceneJSON = { ...scene };

		newSceneJSON.scene.children = newSceneJSON.scene.children.map(
			(child: any) => {
				const newChild = { ...child };
				const newLod = child.lod?.[currentLod];

				if (newLod) {
					newChild.params.src = newLod.src;
				}
				return newChild;
			},
		);
		setCurrent(newSceneJSON);
		setViewpoints(null);
	};

	useEffect(() => {
		if (current) {
			setCorrectLodScene(current);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lodValue]);


	/**
	 * Memoized function to filter and return a list of `objectId` values from the current scene's children.
	 *
	 * - Resets the streaming close event to `null` whenever the scene is loaded or switched.
	 * - Logs the current state of `objectId` values for debugging purposes.
	 * - Filters the children of the current scene to include only those with a defined `objectId` in their `params` and extracts the `objectId` values.
	 *
	 * @returns An array of `objectId` values from the current scene's children that have a defined `objectId`.
	 *
	 * @dependency `current` - The current scene object, which includes its children.
	 */
	const objectIdsInScene = useMemo(() => {
		if (!current || !current?.scene?.children) {
			return [];
		}

		const objectIds = Array.from(
			new Set(
				current.scene.children
					.filter((child: any) => child.params?.objectId !== undefined)
					.map((child: any) => child.params.objectId)
			)
		);

		// console.log("Updated for single fetching: objectId values in this scene", objectIds);

		return objectIds;
		// return ["6666ca9ac3c04c5d1dae1e8f"];
		// return ["666c238c5a732ace8aa247f7"];

	}, [current]);


	/* Checks for errors */

	if (error) {
		logger.debug("[ScenesProvider] Critical error w/scenes", error);
		return <p>Error: {JSON.stringify(error)}</p>;
	}

	if (currentSceneError) {
		logger.debug(
			"[ScenesProvider] Critical error w/ current",
			currentSceneError,
		);
		return <p>Error: {JSON.stringify(currentSceneError)}</p>;
	}

	return (
		<Context.Provider
			value={({
				fetchScenes,
				scenes,
				current,
				setCurrent,
				patchCurrent,
				saveLocationScene,
				saveEditorScene,
				saveSceneAs,
				saveDetailScene,
				savePartialSceneInformation,
				currentLoading,
				loading,
				query,
				updateScene,
				pagedResponse,
				currentBimcollab,
				lodValue,
				setLodValue,
				setCurrentScene,
				availableLods,
				objectIdsInScene,
				objectIdsInWishlists,
				setObjectIdsInWishlists,
				setQuery,
				loadMore,
				createShareLink,
				setCurrentBimcollab: (data: any) => setCurrentBimcollab(data),
				BIMIssues,
				setBIMIssues: (issues: any) => setBIMIssues(issues),
				refreshTopics,
				setRefreshTopics: (value: boolean) => setRefreshTopics(value),

				sendTrackingMessage,

				viewpoints,
				fetchViewpoints,
				setViewpoints,
				goToViewpoint,
				addViewpoint,
				deleteViewpoint,
				updateViewpoint,
				updateViewpointOrder,
				viewpointBeingAdded,
				setViewpointBeingAdded,
				editorState,
				setEditorState,
				editorMode,
				setEditorMode: setEditorModeGuarded,
				isPlanModeAvailable: planModeAvailable,
				linkedFloorplan,

				streamingState: state,
				setStreamingState: setState,
				orgProjScene,
				setOrgProjScene,
				sceneLoaded,
				setSceneLoaded,
				streamingResponse,
				setStreamingResponse,
				arcwareApplication,
				setArcwareApplication,
				pixelStreaming,
				setPixelStreaming,
				applicationResponse,
				setApplicationResponse,
				sendMessage,
				reloadStreamingScene,
				setFlag,
				closeStreaming: closeWebRTCClient,
				streamingCloseEvent,
				setStreamingCloseEvent,
				isStreamingCleaningUp,
				streamingStatus,
			}) as any}
		>
			{children}
		</Context.Provider>
	);
};

export default ScenesProvider;
